import { fetchAuthSession } from 'aws-amplify/auth';
import toast from 'react-hot-toast';
import { isOffline } from './network';

let API_ENDPOINT = process.env.REACT_APP_API_ENDPOINT || '';

export function setApiEndpoint(url) {
  API_ENDPOINT = url || '';
}

async function getHeaders() {
  try {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();
    return {
      'Content-Type': 'application/json',
      Authorization: token || '',
      'X-Timezone': Intl.DateTimeFormat().resolvedOptions().timeZone,
    };
  } catch {
    return { 'Content-Type': 'application/json' };
  }
}

const REQUEST_TIMEOUT_MS = 15000;

async function request(method, path, body, { silent = false } = {}) {
  const headers = await getHeaders();
  const options = { method, headers };
  if (body !== undefined) options.body = JSON.stringify(body);

  // Abort a hung request rather than leaving the caller pending forever — on
  // flaky native connections fetch has no built-in timeout.
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
  let timer;
  if (controller) {
    options.signal = controller.signal;
    timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  }

  const url = `${API_ENDPOINT}${path}`;
  let res;
  try {
    res = await fetch(url, options);
  } catch (networkErr) {
    // Suppress UI noise when the user is offline — Dexie writes still persist
    // locally and the sync engine drains them when the connection returns.
    // Dedup remaining toasts (parallel API calls) by id so one bad route can't
    // stack ten of the same toast. (Timeout aborts land here too.)
    if (!silent && !isOffline()) {
      toast.error('Network error — please check your connection', { id: 'network-error' });
    }
    const err = new Error('Network error');
    err.cause = networkErr;
    throw err;
  } finally {
    if (timer) clearTimeout(timer);
  }

  let data;
  const contentType = res.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    data = await res.json().catch(() => ({}));
  } else {
    data = await res.text();
  }

  if (res.status === 401) {
    if (!silent) window.location.href = '/login';
    const err = new Error(typeof data === 'object' ? data.message || data.error || 'Unauthorized' : 'Unauthorized');
    err.status = 401;
    throw err;
  }

  if (!res.ok) {
    const message = (typeof data === 'object' ? (data.message || data.error) : null) || 'Request failed';
    if (!silent) {
      // Dedup repeated server errors across parallel API calls — collapse to a single toast.
      // Bucket 5xx vs 4xx separately so a stray 404 doesn't blot out a backend outage notice.
      const toastId = res.status >= 500 ? 'server-error' : `api-${res.status}`;
      toast.error(message, { id: toastId });
    }
    const err = new Error(message);
    err.status = res.status;
    err.code = typeof data === 'object' ? data.error : undefined;
    err.data = data;
    throw err;
  }

  return data;
}

export const habitsApi = {
  list: () => request('GET', '/habits'),
  create: (habit) => request('POST', '/habits', habit),
  update: (habitId, updates) => request('PUT', `/habits/${habitId}`, updates),
  delete: (habitId) => request('DELETE', `/habits/${habitId}`),
  checkin: (habitId, date) => request('POST', `/habits/${habitId}/checkin`, { date }),
  undoCheckin: (habitId, date) => request('DELETE', `/habits/${habitId}/checkin/${date}`),
  history: (habitId, from, to) => {
    const params = new URLSearchParams();
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    const qs = params.toString();
    return request('GET', `/habits/${habitId}/history${qs ? `?${qs}` : ''}`);
  },
  freeze: (habitId) => request('POST', `/habits/${habitId}/freeze`),
};

export const statsApi = {
  overview: () => request('GET', '/stats/overview'),
  habit: (habitId) => request('GET', `/stats/${habitId}`),
  export: (format) => request('GET', `/stats/export${format ? `?format=${format}` : ''}`),
};

export const subscriptionApi = {
  status: () => request('GET', '/subscription/status'),
  appleVerify: (transactionId) => request('POST', '/subscription/apple-verify', { transactionId }, { silent: true }),
};

export const userApi = {
  deleteAccount: () => request('DELETE', '/user/account'),
};

// Direct fetch for Apple Sign In (route is unauthenticated — no JWT header).
export async function postAppleNative(body) {
  const res = await fetch(`${API_ENDPOINT}/auth/apple-native`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data.error || data.message || 'Apple sign-in failed');
    err.status = res.status;
    throw err;
  }
  return data;
}

export { API_ENDPOINT };
